import { isResource, resolveId } from '@medplum/core'
import type {
  Patient,
  Practitioner,
  Quantity,
  Reference,
  ServiceRequest,
} from '@medplum/fhirtypes'
import { codeableConcept, hasCodeableConcept } from '#src/codeableConcept.ts'
import type { ExportFields } from '#src/ExtendedResource.ts'
import {
  INTERNAL_ORDER_CODING,
  type OrderCategory,
  type OrderStatus,
} from '#src/models/orders/constants.ts'
import { Order } from '#src/models/orders/Order.ts'
import { isOfType, now } from '#src/utils.ts'

export class InternalOrder extends Order {
  constructor({
    requester,
    category,
    description,
    status,
    preferredVendor,
    priority,
    ...rest
  }: Omit<
    ServiceRequest,
    | 'resourceType'
    | 'category'
    | 'intent'
    | 'subject'
    | 'status'
    | 'requester'
    | 'priority'
  > & {
    participant: Reference<Patient>
    requester?: Reference<Practitioner>
    category?: OrderCategory
    description?: string
    status?: OrderStatus
    preferredVendor?: string
    priority?: ServiceRequest['priority']
  }) {
    super({
      authoredOn: now(),
      code: codeableConcept(INTERNAL_ORDER_CODING),
      status: status ?? 'draft',
      ...rest,
    })
    if (priority) {
      // InternalOrder gets FHIR priority values, convert to priorityTimeframe
      const priorityTimeframe = `${priority}-30` as const
      this.priority = priorityTimeframe as any
    }
    if (category) {
      this.category = category
    }
    if (description) {
      this.description = description
    }
    if (preferredVendor) {
      this.preferredVendor = preferredVendor
    }
    if (requester) {
      this.requester = requester
    }
  }

  static override isInstance(resource: unknown): resource is ServiceRequest {
    return (
      isResource(resource) &&
      isOfType(resource, 'ServiceRequest') &&
      hasCodeableConcept(resource.code, {
        coding: [INTERNAL_ORDER_CODING],
      })
    )
  }

  get quantity(): Quantity {
    return this.resource.quantityQuantity as Quantity
  }

  set quantity(quantity: Quantity) {
    this.resource.quantityQuantity = quantity
  }

  get authoredOn(): string | undefined {
    return this.resource.authoredOn
  }

  get notesSummary(): { time?: string; text?: string; authorId?: string }[] {
    return this.notes.map((n) => ({
      time: n.time,
      text: n.text,
      authorId: resolveId(n.authorReference),
    }))
  }

  protected getExportFields(): ExportFields {
    return {
      quantity: this.quantity?.value,
      unit: this.quantity?.unit,
      notes: this.notesSummary,
    }
  }
}
